import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";

const PILLARS = [
  {
    n: "01",
    word: "STRATEGY",
    blurb: "Direction before decoration — every creative move starts with a reason.",
    img: "/images/work-3.webp",
  },
  {
    n: "02",
    word: "CREATIVITY",
    blurb: "Ideas engineered to be remembered, not scrolled past.",
    img: "/images/work-1.webp",
  },
  {
    n: "03",
    word: "TECHNOLOGY",
    blurb: "AI and modern tooling woven into every stage of the craft.",
    img: "/images/work-5.webp",
  },
  {
    n: "04",
    word: "GROWTH",
    blurb: "Systems that compound — audiences, pipelines, momentum.",
    img: "/images/work-6.webp",
  },
];

export const Positioning = () => {
  const [active, setActive] = useState(0);

  return (
    <section data-testid="positioning-section" className="relative py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-end mb-20 lg:mb-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
              The BrandZenith Standard
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
              Your brand deserves more than{" "}
              <span className="text-gradient-purple">just content.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base sm:text-lg text-[#A6A6B0] leading-relaxed lg:pl-10 lg:border-l lg:border-white/10">
              We combine brand strategy, creative design, digital experiences and artificial
              intelligence to help businesses build brands that are impossible to ignore.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-20 items-start">
          <div>
            {PILLARS.map((p, i) => {
              const isActive = active === i;
              return (
                <Reveal key={p.n} delay={i * 0.06} y={30}>
                  <div
                    data-testid={`pillar-${p.word.toLowerCase()}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className={`group cursor-pointer border-t border-white/[0.07] last:border-b transition-colors duration-500 ${
                      isActive ? "bg-white/[0.02]" : ""
                    }`}
                  >
                    <div
                      className={`flex items-baseline gap-6 sm:gap-10 py-8 sm:py-10 px-4 sm:px-6 transition-transform duration-500 ${
                        isActive ? "translate-x-2" : ""
                      }`}
                    >
                      <span
                        className={`font-display text-xs sm:text-sm font-bold tracking-[0.2em] transition-colors duration-500 ${
                          isActive ? "text-[#C43CFF]" : "text-[#8B2CFF]/70"
                        }`}
                      >
                        {p.n}
                      </span>
                      <span
                        className={`font-display text-[9vw] sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-none transition-colors duration-500 ${
                          isActive ? "text-white" : "text-white/15"
                        }`}
                      >
                        {p.word}
                      </span>
                    </div>
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden lg:hidden"
                        >
                          <div className="px-4 sm:px-6 pb-8">
                            <div className="rounded-lg overflow-hidden border border-white/10 mb-4">
                              <img
                                src={p.img}
                                alt={`${p.word} — BrandZenith pillar visual`}
                                loading="lazy"
                                className="w-full aspect-[16/9] object-cover"
                              />
                            </div>
                            <p className="text-sm text-[#A6A6B0] leading-relaxed">{p.blurb}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2} className="hidden lg:block sticky top-28">
            <div
              data-testid="pillar-preview"
              className="relative aspect-[4/5] rounded-lg overflow-hidden border border-white/[0.08]"
            >
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.n}
                  className="absolute inset-0"
                  animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.06 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                >
                  <img
                    src={p.img}
                    alt={`${p.word} — BrandZenith pillar visual`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-[#050507]/90 via-[#050507]/10 to-transparent"
                    aria-hidden="true"
                  />
                </motion.div>
              ))}
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={active}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4 }}
                    className="text-sm text-white/85 leading-relaxed"
                  >
                    {PILLARS[active].blurb}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
